/**
 * One selected element + one bulk op → the single call that carries it out.
 * For Media Devoted | Created By Ciro Autiero
 *
 * These are the plain service functions, not the mutation hooks, on purpose:
 * each hook invalidates the queue on success, and this endpoint re-reads the
 * tracker and rebuilds the metrics cube. Running twelve of them would mean
 * twelve rebuilds. The caller invalidates once when the batch is done.
 */

import type { QueueElement } from '@trafficpulse/shared';

import api from '@/services/api';
import type { QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';

import {
  demoteElement,
  promoteToLive,
  requeueElement,
} from '../../../../services/flow-optimizer-queue-api';
import { BANK_PATH } from '../../../../hooks/useQueuePriority';
import type { BulkOp } from '../bulk-actions';

export async function performBulkOp(
  op: BulkOp,
  campaignId: string,
  provider: QueueProvider,
  el: QueueElement,
): Promise<unknown> {
  const ref = { layer: el.layer, bankId: el.bankId };
  switch (op.kind) {
    case 'promote':
      // allowReplace:false — a bulk selection must never cost a live element
      // the user did not name. The server refuses instead of evicting.
      return promoteToLive(
        campaignId,
        { ...ref, allowReplace: false },
        provider,
      );
    case 'revive-and-promote':
      // Sequential on purpose: promote refuses a tombstoned element, so the
      // revive has to land first. allowReplace:false for the same reason as
      // above — a bulk must never cost a live element the user didn't name.
      await requeueElement(campaignId, { ...ref, to: 'queue' }, provider);
      return promoteToLive(campaignId, { ...ref, allowReplace: false }, provider);
    case 'demote':
      return demoteElement(campaignId, { ...ref, to: op.to }, provider);
    case 'requeue':
      return requeueElement(campaignId, { ...ref, to: op.to }, provider);
    case 'mark-ready':
      return api.patch(`/voluum/${BANK_PATH[el.layer]}/${el.bankId}`, {
        queueReady: op.ready,
      });
  }
}
